import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, IdCard, Lock, Loader2, UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { employeeLogin } from "@/api";
import { useAuth } from "@/context/AuthContext";

export default function EmployeeLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [employeeId, setEmployeeId] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!employeeId.trim() || !password) {
      toast.error("Please enter your Employee ID and password");
      return;
    }
    setLoading(true);
    try {
      const res = await employeeLogin(employeeId.trim(), password);
      if (res.data?.success && res.data.token) {
        login(res.data.token, res.data.employee, 'employee');
        toast.success(`Welcome, ${res.data.employee.name}`);
        navigate('/employee', { replace: true });
      } else {
        toast.error('Invalid credentials');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-cyan-50/20 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4" /> Back to home
        </button>

        <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-8">
          <div className="flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1565C0] to-[#0D47A1] mb-5 shadow-md shadow-[#1565C0]/30">
            <UserCheck className="h-8 w-8 text-white" strokeWidth={2} />
          </div>
          <h1
            className="text-2xl font-black text-slate-900 mb-1 tracking-tight"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Employee Login
          </h1>
          <p className="text-sm text-slate-500 mb-6">
            Access your work dashboard with your Employee ID.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-2 block">
                Employee ID
              </label>
              <div className="relative">
                <IdCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value)}
                  placeholder="e.g. EMP001"
                  className="pl-10 rounded-xl border-slate-200 focus:ring-2 focus:ring-[#1565C0]/20"
                  autoComplete="username"
                  data-testid="employee-id-input"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-2 block">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your password"
                  className="pl-10 rounded-xl border-slate-200 focus:ring-2 focus:ring-[#1565C0]/20"
                  autoComplete="current-password"
                  data-testid="employee-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-[#1565C0] to-[#0D47A1] hover:from-[#0D47A1] hover:to-[#0D47A1] text-white py-4 rounded-xl font-bold shadow-md shadow-[#1565C0]/20"
              data-testid="employee-login-submit"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Log In"}
            </Button>
          </form>

          <p className="text-xs text-center text-slate-400 mt-6">
            Trouble logging in? Contact your administrator.
          </p>
        </div>
      </div>
    </div>
  );
}
